import React, { useEffect, useState } from 'react';
import { appointmentService, Professional } from '../services/appointmentService';
import { Plus, Edit2, Trash2, User, X, Camera } from 'lucide-react';
import ImageCropper from './ImageCropper';

interface Props {
  branch?: 'antofagasta' | 'santiago';
}

const ProfessionalList: React.FC<Props> = ({ branch = 'antofagasta' }) => {
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Professional | null>(null);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
  });
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [saving, setSaving] = useState(false);

  // Estados para manejo de imagen
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [croppedBlob, setCroppedBlob] = useState<Blob | null>(null);

  useEffect(() => {
    loadProfessionals();
  }, [branch]);

  const loadProfessionals = async () => {
    setLoading(true);
    try {
      const data = branch === 'santiago'
        ? await appointmentService.getAllProfessionalsSantiago()
        : await appointmentService.getAllProfessionals();
      setProfessionals(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error cargando profesionales', err);
      setProfessionals([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setForm({ first_name: '', last_name: '', email: '', phone_number: '' });
    setEditing(null);
    setErrors({});
    setPreviewUrl(null);
    setCroppedBlob(null);
    setSelectedFile(null);
    setShowForm(true);
  };

  const handleEdit = (prof: Professional) => {
    setForm({
      first_name: prof.first_name,
      last_name: prof.last_name,
      email: prof.email || '',
      phone_number: prof.phone_number || '',
    });
    setEditing(prof);
    setErrors({});
    setPreviewUrl(prof.image || null);
    setCroppedBlob(null);
    setSelectedFile(null);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Eliminar este profesional?')) return;
    try {
      if (branch === 'santiago') {
        await appointmentService.deleteProfessionalSantiago(id);
      } else {
        await appointmentService.deleteProfessional(id);
      }
      await loadProfessionals();
    } catch (err) {
      console.error('Error eliminando profesional', err);
      alert('No se pudo eliminar el profesional');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy[name];
        return copy;
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedFile(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCrop = (blob: Blob) => {
    setCroppedBlob(blob);
    setPreviewUrl(URL.createObjectURL(blob));
    setSelectedFile(null); // Cerrar modal
  };

  const handleCancelCrop = () => {
    setSelectedFile(null);
  };

  const validate = () => {
    const e: { [k: string]: string } = {};
    if (!form.first_name.trim()) e.first_name = 'El nombre es obligatorio';
    if (!form.last_name.trim()) e.last_name = 'El apellido es obligatorio';
    return e;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setSaving(true);
    
    try {
      const formData = new FormData();
      formData.append('first_name', form.first_name);
      formData.append('last_name', form.last_name);
      if (form.email) formData.append('email', form.email);
      if (form.phone_number) formData.append('phone_number', form.phone_number);
      
      if (croppedBlob) {
        formData.append('image', croppedBlob, 'profile.jpg');
      }

      if (editing) {
        branch === 'santiago'
          ? await appointmentService.updateProfessionalSantiago(editing.id, formData)
          : await appointmentService.updateProfessional(editing.id, formData);
      } else {
        branch === 'santiago'
          ? await appointmentService.createProfessionalSantiago(formData)
          : await appointmentService.createProfessional(formData);
      }
      setShowForm(false);
      await loadProfessionals();
    } catch (err: any) {
      console.error('Error guardando profesional', err);
      const data = err?.response?.data;
      if (data && typeof data === 'object') {
        const next: { [k: string]: string } = {};
        Object.keys(data).forEach((k) => {
          const v = (data as any)[k];
          next[k] = Array.isArray(v) ? String(v[0]) : String(v);
        });
        setErrors(next);
      } else {
        alert('Error al guardar el profesional');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {selectedFile && (
        <ImageCropper
          imageSrc={selectedFile}
          onCrop={handleCrop}
          onCancel={handleCancelCrop}
        />
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Profesionales</h2>
        <button
          onClick={handleCreate}
          className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors duration-200 shadow-sm"
        >
          <Plus className="w-4 h-4" />
          <span>Nuevo Profesional</span>
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">{editing ? 'Editar Profesional' : 'Crear Profesional'}</h3>
            <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Sección de Foto */}
            <div className="flex flex-col items-center justify-center mb-6">
              <div className="relative group">
                <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 border-2 border-gray-200 flex items-center justify-center">
                  {previewUrl ? (
                    <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-12 h-12 text-gray-400" />
                  )}
                </div>
                <label className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full cursor-pointer hover:bg-blue-700 shadow-lg transition-transform transform hover:scale-105">
                  <Camera className="w-4 h-4" />
                  <input 
                    type="file" 
                    className="hidden" 
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </label>
              </div>
              <p className="text-sm text-gray-500 mt-2">Foto de perfil</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input
                  name="first_name"
                  value={form.first_name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${errors.first_name ? 'border-red-500' : 'border-gray-300'} rounded-lg`}
                />
                {errors.first_name && <p className="text-sm text-red-600 mt-1">{errors.first_name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Apellido</label>
                <input
                  name="last_name"
                  value={form.last_name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${errors.last_name ? 'border-red-500' : 'border-gray-300'} rounded-lg`}
                />
                {errors.last_name && <p className="text-sm text-red-600 mt-1">{errors.last_name}</p>}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email (opcional)</label>
                <input
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono (opcional)</label>
                <input
                  name="phone_number"
                  value={form.phone_number}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-4 border-t border-gray-100">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                Cancelar
              </button>
              <button type="submit" disabled={saving} className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors">
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {loading ? (
          <div className="p-6 text-center">Cargando profesionales...</div>
        ) : professionals.length === 0 ? (
          <div className="p-6 text-center text-gray-600">No hay profesionales creados aún.</div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {professionals.map((prof) => (
              <li key={prof.id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
                    {prof.image ? (
                      <img src={prof.image} alt={prof.first_name} className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 text-lg">
                      {prof.first_name} {prof.last_name}
                    </div>
                    <div className="text-sm text-gray-500 flex flex-col sm:flex-row sm:gap-4">
                      {prof.email && <span className="flex items-center gap-1">✉️ {prof.email}</span>}
                      {prof.phone_number && <span className="flex items-center gap-1">📱 {prof.phone_number}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(prof)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Editar"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(prof.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ProfessionalList;
